import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ApiClient from '../config/ApiClient';

interface RecordingSession {
    lessonPlanId: number;
    sessionId?: number;
    slideId?: number;
    startedAt: Date;
    mediaRecorder?: MediaRecorder;
    stream?: MediaStream;
}

interface QueuedRecording {
    blob: Blob;
    metadata: {
        lessonPlanId: number;
        sessionId?: number;
        slideId?: number;
        startedAt: Date;
        endedAt: Date;
        duration: number;
    };
    retryCount: number;
}

interface LessonPlanRecordingContextType {
    // State
    currentRecording: RecordingSession | null;
    isRecording: () => boolean;
    isStarted: boolean; // true once recording has been started this session (used to auto upload+restart between slides)
    getCurrentRecording: () => RecordingSession | null;
    uploadQueue: QueuedRecording[];
    uploading: boolean;

    // Single active session and slide by ID (only one accordion and one slide open at a time)
    activeSessionId: number | null;
    activeSlideId: number | null;
    setActiveSessionId: (id: number | null) => void;
    setActiveSlideId: (id: number | null) => void;

    // Methods
    startRecording: (lessonPlanId: number, sessionId?: number, slideId?: number, fromUser?: boolean) => Promise<void>;
    stopRecording: (fromUser?: boolean, discard?: boolean) => Promise<void>;
    uploadRecording: (blob: Blob, metadata: QueuedRecording['metadata']) => Promise<void>;
    /** Wait until upload queue is empty and no upload in flight (e.g. before leaving the page after stop). */
    waitForUploadQueueIdle: () => Promise<void>;
    /** Discard active recording (if any) and clear pending uploads — data loss. */
    abandonRecordingAndUploads: () => Promise<void>;
}

const LessonPlanRecordingContext = createContext<LessonPlanRecordingContextType | null>(null);

/** Minimum duration (seconds) to upload – recordings shorter than this are skipped to avoid silent/tiny files from quick slide clicks */
const MIN_RECORDING_DURATION_SECONDS = 3;
/** Minimum blob size (bytes) – smaller blobs are likely empty or header-only, skip upload */
const MIN_BLOB_SIZE_BYTES = 1024;

function shouldUploadRecording(blob: Blob, durationSeconds: number): boolean {
    return durationSeconds >= MIN_RECORDING_DURATION_SECONDS && blob.size >= MIN_BLOB_SIZE_BYTES;
}

export function useLessonPlanRecording() {
    const context = useContext(LessonPlanRecordingContext);
    if (!context) {
        throw new Error('useLessonPlanRecording must be used within LessonPlanRecordingProvider');
    }
    return context;
}

interface Props {
    children: ReactNode;
}

export function LessonPlanRecordingProvider({ children }: Props) {
    const [currentRecording, setCurrentRecording] = useState<RecordingSession | null>(null);
    const [isStarted, setIsStarted] = useState(false);
    const [uploadQueue, setUploadQueue] = useState<QueuedRecording[]>([]);
    const [uploading, setUploading] = useState(false);
    const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
    const [activeSlideId, setActiveSlideId] = useState<number | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    /** Must accumulate here; ondataavailable must be set before MediaRecorder.start() or timeslice data is lost. */
    const recordingChunksRef = useRef<BlobPart[]>([]);
    const uploadQueueRef = useRef<QueuedRecording[]>([]);
    const uploadingRef = useRef(false);

    // RECORDER: trace state changes
    useEffect(() => {
        console.log('RECORDER currentRecording', currentRecording ? { lessonPlanId: currentRecording.lessonPlanId, sessionId: currentRecording.sessionId, slideId: currentRecording.slideId, startedAt: currentRecording.startedAt?.toISOString() } : null);
    }, [currentRecording]);
    useEffect(() => {
        console.log('RECORDER isStarted', isStarted);
    }, [isStarted]);
    useEffect(() => {
        console.log('RECORDER uploadQueue', uploadQueue.length, uploadQueue.map((q, i) => ({ i, lessonPlanId: q.metadata.lessonPlanId, sessionId: q.metadata.sessionId, slideId: q.metadata.slideId, duration: q.metadata.duration, retryCount: q.retryCount })));
    }, [uploadQueue]);
    uploadQueueRef.current = uploadQueue;
    useEffect(() => {
        console.log('RECORDER uploading', uploading);
    }, [uploading]);
    uploadingRef.current = uploading;
    useEffect(() => {
        console.log('RECORDER activeSessionId', activeSessionId);
    }, [activeSessionId]);
    useEffect(() => {
        console.log('RECORDER activeSlideId', activeSlideId);
    }, [activeSlideId]);

    const isRecording = useCallback(() => {
        return currentRecording !== null && mediaRecorderRef.current?.state === 'recording';
    }, [currentRecording]);

    const getCurrentRecording = useCallback(() => {
        return currentRecording;
    }, [currentRecording]);

    const stopCurrentRecording = useCallback(async (): Promise<Blob | null> => {
        if (!mediaRecorderRef.current || !currentRecording) {
            console.log('RECORDER stopCurrentRecording early return', { hasMediaRecorder: !!mediaRecorderRef.current, hasCurrentRecording: !!currentRecording });
            return null;
        }
        console.log('RECORDER stopCurrentRecording', { state: mediaRecorderRef.current?.state });

        return new Promise((resolve) => {
            if (mediaRecorderRef.current) {
                mediaRecorderRef.current.onstop = () => {
                    const blob = new Blob(recordingChunksRef.current, { type: 'audio/webm' });
                    console.log('RECORDER stopCurrentRecording resolved', { blobSize: blob.size });
                    resolve(blob);
                };

                if (mediaRecorderRef.current.state === 'recording') {
                    // Flush pending data before stop – some browsers deliver empty chunks otherwise
                    mediaRecorderRef.current.requestData();
                    mediaRecorderRef.current.stop();
                } else {
                    console.log('RECORDER stopCurrentRecording MediaRecorder not recording, resolve null');
                    resolve(null);
                }
            }
        });
    }, [currentRecording]);

    const cleanupStream = useCallback(() => {
        console.log('RECORDER cleanupStream', { hadStream: !!streamRef.current, hadMediaRecorder: !!mediaRecorderRef.current });
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        mediaRecorderRef.current = null;
    }, []);

    const startRecording = useCallback(async (
        lessonPlanId: number,
        sessionId?: number,
        slideId?: number,
        fromUser?: boolean
    ): Promise<void> => {
        console.log('RECORDER startRecording', { lessonPlanId, sessionId, slideId, hadCurrent: !!currentRecording, fromUser });
        try {
            // Stop any existing recording first
            if (currentRecording) {
                console.log('RECORDER startRecording stopping previous', { lessonPlanId: currentRecording.lessonPlanId, sessionId: currentRecording.sessionId, slideId: currentRecording.slideId });
                const blob = await stopCurrentRecording();
                if (blob && currentRecording) {
                    const endedAt = new Date();
                    const duration = Math.floor((endedAt.getTime() - currentRecording.startedAt.getTime()) / 1000);

                    if (shouldUploadRecording(blob, duration)) {
                        // Queue the previous recording for upload
                        await uploadRecording(blob, {
                        lessonPlanId: currentRecording.lessonPlanId,
                        sessionId: currentRecording.sessionId,
                        slideId: currentRecording.slideId,
                        startedAt: currentRecording.startedAt,
                        endedAt,
                        duration
                    });
                    } else {
                        console.log('RECORDER startRecording skipping upload (too short or empty)', { duration, blobSize: blob.size });
                    }
                }
                cleanupStream();
            }

            // Request media stream
            console.log('RECORDER startRecording getUserMedia');
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false
            });

            streamRef.current = stream;

            // Create MediaRecorder
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus'
            });

            mediaRecorderRef.current = mediaRecorder;

            recordingChunksRef.current = [];
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    recordingChunksRef.current.push(event.data);
                }
            };

            // Start recording with 1s timeslice so ondataavailable fires periodically (avoids empty final chunk on some browsers)
            mediaRecorder.start(1000);

            const startedAt = new Date();
            console.log('RECORDER startRecording started', { lessonPlanId, sessionId, slideId, startedAt: startedAt.toISOString() });

            setCurrentRecording({
                lessonPlanId,
                sessionId,
                slideId,
                startedAt,
                mediaRecorder,
                stream
            });
            if (fromUser) setIsStarted(true);
        } catch (error: any) {
            console.error('RECORDER startRecording error', error);
            cleanupStream();
            throw error;
        }
    }, [currentRecording, stopCurrentRecording, cleanupStream]);

    const stopRecording = useCallback(async (fromUser?: boolean, discard?: boolean): Promise<void> => {
        console.log('RECORDER stopRecording', { hasCurrent: !!currentRecording, fromUser, discard, current: currentRecording ? { lessonPlanId: currentRecording.lessonPlanId, sessionId: currentRecording.sessionId, slideId: currentRecording.slideId } : null });
        if (!currentRecording) {
            console.log('RECORDER stopRecording early return (no current)');
            if (fromUser) setIsStarted(false);
            return;
        }

        const blob = await stopCurrentRecording();
        const endedAt = new Date();
        const duration = currentRecording ? Math.floor((endedAt.getTime() - currentRecording.startedAt.getTime()) / 1000) : 0;

        if (blob && !discard) {
            if (shouldUploadRecording(blob, duration)) {
                console.log('RECORDER stopRecording queuing', { duration, blobSize: blob.size });
                await uploadRecording(blob, {
                lessonPlanId: currentRecording.lessonPlanId,
                sessionId: currentRecording.sessionId,
                slideId: currentRecording.slideId,
                startedAt: currentRecording.startedAt,
                endedAt,
                duration
            });
            } else {
                console.log('RECORDER stopRecording skipping upload (too short or empty)', { duration, blobSize: blob.size });
            }
        } else if (discard) {
            console.log('RECORDER stopRecording discard, not queuing');
        } else {
            console.log('RECORDER stopRecording no blob, skipping queue');
        }

        cleanupStream();
        setCurrentRecording(null);
        if (fromUser) setIsStarted(false);
        console.log('RECORDER stopRecording done', fromUser ? 'isStarted=false' : 'currentRecording=null');
    }, [currentRecording, stopCurrentRecording, cleanupStream]);

    const waitForUploadQueueIdle = useCallback((): Promise<void> => {
        return new Promise((resolve, reject) => {
            const deadline = Date.now() + 120000;
            const tick = () => {
                if (uploadQueueRef.current.length === 0 && !uploadingRef.current) {
                    resolve();
                    return;
                }
                if (Date.now() > deadline) {
                    reject(new Error('Upload queue did not drain within 120s'));
                    return;
                }
                window.setTimeout(tick, 100);
            };
            tick();
        });
    }, []);

    const abandonRecordingAndUploads = useCallback(async (): Promise<void> => {
        await stopRecording(true, true);
        setUploadQueue([]);
    }, [stopRecording]);

    const uploadRecording = useCallback(async (
        blob: Blob,
        metadata: QueuedRecording['metadata']
    ): Promise<void> => {
        console.log('RECORDER uploadRecording queue add', { lessonPlanId: metadata.lessonPlanId, sessionId: metadata.sessionId, slideId: metadata.slideId, duration: metadata.duration, blobSize: blob.size });
        const queuedRecording: QueuedRecording = {
            blob,
            metadata,
            retryCount: 0
        };

        setUploadQueue(prev => [...prev, queuedRecording]);
    }, []);

    const processUploadQueue = useCallback(async () => {
        if (uploading || uploadQueue.length === 0) {
            console.log('RECORDER processUploadQueue skip', { uploading, queueLength: uploadQueue.length });
            return;
        }

        const recording = uploadQueue[0];
        console.log('RECORDER processUploadQueue start', { lessonPlanId: recording.metadata.lessonPlanId, sessionId: recording.metadata.sessionId, slideId: recording.metadata.slideId, retryCount: recording.retryCount });
        setUploading(true);

        try {
            const formData = new FormData();
            formData.append('recording', recording.blob, `recording-${Date.now()}.webm`);
            formData.append('lesson_plan_id', recording.metadata.lessonPlanId.toString());

            if (recording.metadata.sessionId) {
                formData.append('session_id', recording.metadata.sessionId.toString());
            }
            if (recording.metadata.slideId) {
                formData.append('slide_id', recording.metadata.slideId.toString());
            }

            formData.append('duration', recording.metadata.duration.toString());
            if (recording.metadata.startedAt) {
                formData.append('started_at', recording.metadata.startedAt.toISOString());
            }
            if (recording.metadata.endedAt) {
                formData.append('ended_at', recording.metadata.endedAt.toISOString());
            }

            const response = await ApiClient.post(
                '/api/session-recordings/upload_recording/',
                formData
            );

            if (response.success) {
                console.log('RECORDER processUploadQueue success', { lessonPlanId: recording.metadata.lessonPlanId, sessionId: recording.metadata.sessionId, slideId: recording.metadata.slideId });
                // Remove from queue
                setUploadQueue(prev => prev.slice(1));
            } else {
                // Retry logic
                if (recording.retryCount < 3) {
                    console.log('RECORDER processUploadQueue retry', { retryCount: recording.retryCount + 1, error: response.error });
                    setUploadQueue(prev => prev.map((item, index) =>
                        index === 0 ? { ...item, retryCount: item.retryCount + 1 } : item
                    ));
                    // Retry after 2 seconds
                    setTimeout(() => processUploadQueue(), 2000);
                } else {
                    // Max retries reached, remove from queue
                    console.error('RECORDER processUploadQueue max retries', { error: response.error });
                    setUploadQueue(prev => prev.slice(1));
                }
            }
        } catch (error: any) {
            console.error('RECORDER processUploadQueue catch', error);
            // Retry logic
            if (recording.retryCount < 3) {
                console.log('RECORDER processUploadQueue retry after catch', { retryCount: recording.retryCount + 1 });
                setUploadQueue(prev => prev.map((item, index) =>
                    index === 0 ? { ...item, retryCount: item.retryCount + 1 } : item
                ));
                setTimeout(() => processUploadQueue(), 2000);
            } else {
                console.error('RECORDER processUploadQueue max retries (catch)');
                setUploadQueue(prev => prev.slice(1));
            }
        } finally {
            setUploading(false);
            console.log('RECORDER processUploadQueue uploading=false');
        }
    }, [uploadQueue, uploading]);

    // Process upload queue when it changes
    useEffect(() => {
        if (uploadQueue.length > 0 && !uploading) {
            console.log('RECORDER processUploadQueue effect triggered', { queueLength: uploadQueue.length });
            processUploadQueue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadQueue.length, uploading]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            console.log('RECORDER unmount cleanup', { wasRecording: mediaRecorderRef.current?.state === 'recording' });
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            cleanupStream();
        };
    }, [cleanupStream]);

    const value: LessonPlanRecordingContextType = {
        currentRecording,
        isRecording,
        isStarted,
        getCurrentRecording,
        uploadQueue,
        uploading,
        activeSessionId,
        activeSlideId,
        setActiveSessionId,
        setActiveSlideId,
        startRecording,
        stopRecording,
        uploadRecording,
        waitForUploadQueueIdle,
        abandonRecordingAndUploads
    };

    return (
        <LessonPlanRecordingContext.Provider value={value}>
            {children}
        </LessonPlanRecordingContext.Provider>
    );
}
